import React, { useState } from "react";
import { submitLead } from "@/lib/api";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

const PACKAGE_OPTIONS = [
  { value: "top-of-mind", label: "TV Broadcast ($4,500)" },
  { value: "call-now", label: "Desktop & Mobile Ads ($4,500)" },
  { value: "social-spotlight", label: "Social Media ($4,500)" },
  { value: "market-dominator", label: "Omni-Channel ($9,000)" },
  { value: "not-sure", label: "Not sure yet, recommend one" },
];

const LeadForm = ({ trade = "general", tradeName = "Home Services", variant = "dark", headline }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    package_interest: "not-sure",
    message: "",
  });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      await submitLead({
        ...form,
        trade,
        source_page: window.location.pathname,
      });
      setStatus({ state: "success", error: "" });
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        package_interest: "not-sure",
        message: "",
      });
    } catch (err) {
      setStatus({
        state: "error",
        error:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong. Please try again.",
      });
    }
  };

  const isLight = variant === "light";
  const inputCls = isLight ? "af-input-light" : "af-input";
  const wrapCls = isLight
    ? "surface-cream p-6 sm:p-8 space-y-4"
    : "surface p-6 sm:p-8 space-y-4";
  const labelCls = isLight
    ? "block font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-navy)]/65 mb-1.5"
    : "block font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-cream)]/65 mb-1.5";

  if (status.state === "success") {
    return (
      <div
        className="surface-cream p-8"
        data-testid="lead-form-success"
      >
        <div className="w-10 h-10 rounded-full bg-[var(--af-lime)] flex items-center justify-center mb-4">
          <CheckCircle2 size={22} className="text-[var(--af-navy)]" />
        </div>
        <h3 className="font-display text-3xl mb-2 text-[var(--af-navy)]">
          You're on the list.
        </h3>
        <p className="text-sm text-[var(--af-navy)]/75 leading-relaxed">
          Anne will reach out within one business day to schedule your free
          20-minute market audit for <strong>{tradeName}</strong>. In the
          meantime, go put out a fire. We'll handle the marketing.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={wrapCls} data-testid="lead-form">
      <div>
        <div className={isLight ? "eyebrow-dark" : "eyebrow"}>
          Book Your Free 20-Min Market Audit
        </div>
        <h3
          className={`font-display text-3xl mt-3 ${isLight ? "text-[var(--af-navy)]" : ""}`}
        >
          {headline || `Tell us about your ${tradeName.toLowerCase()} business.`}
        </h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field
          label="Your name *"
          name="name"
          value={form.name}
          onChange={onChange}
          required
          testid="lead-input-name"
          inputCls={inputCls}
          labelCls={labelCls}
        />
        <Field
          label="Company name *"
          name="company"
          value={form.company}
          onChange={onChange}
          required
          testid="lead-input-company"
          inputCls={inputCls}
          labelCls={labelCls}
        />
        <Field
          label="Email *"
          name="email"
          type="email"
          value={form.email}
          onChange={onChange}
          required
          testid="lead-input-email"
          inputCls={inputCls}
          labelCls={labelCls}
        />
        <Field
          label="Phone"
          name="phone"
          value={form.phone}
          onChange={onChange}
          testid="lead-input-phone"
          inputCls={inputCls}
          labelCls={labelCls}
        />
      </div>

      <div>
        <label className={labelCls}>Which package interests you most?</label>
        <select
          name="package_interest"
          value={form.package_interest}
          onChange={onChange}
          className={inputCls}
          data-testid="lead-input-package"
        >
          {PACKAGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>
          Anything else? (Service area, goals, etc.)
        </label>
        <textarea
          name="message"
          rows={3}
          value={form.message}
          onChange={onChange}
          className={`${inputCls} resize-none`}
          placeholder="e.g. We're an HVAC shop in Crozet looking to grow tune-up volume this spring."
          data-testid="lead-input-message"
        />
      </div>

      {status.state === "error" && (
        <div
          className="flex gap-2 items-start text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md"
          data-testid="lead-form-error"
        >
          <AlertCircle size={16} className="mt-0.5" />
          <span>{status.error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status.state === "submitting"}
        className="btn-lime w-full justify-center py-3.5 disabled:opacity-60"
        data-testid="lead-submit-btn"
      >
        {status.state === "submitting" ? (
          "Sending..."
        ) : (
          <>
            Book My Free Audit <ArrowRight size={14} />
          </>
        )}
      </button>
      <p
        className={`text-[11px] text-center ${isLight ? "text-[var(--af-navy)]/55" : "text-[var(--af-cream)]/45"}`}
      >
        No spam. No pressure. Just a 20-minute conversation about your market.
      </p>
    </form>
  );
};

const Field = ({ label, testid, inputCls, labelCls, ...props }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input {...props} data-testid={testid} className={inputCls} />
  </div>
);

export default LeadForm;
